import * as React from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import bootstrap5Plugin from '@fullcalendar/bootstrap5';
import interactionPlugin from '@fullcalendar/interaction';
import { DatesSetArg, DateSelectArg, DateSpanApi, DayCellContentArg } from '@fullcalendar/core';
import esLocale from '@fullcalendar/core/locales/es';
import frLocale from '@fullcalendar/core/locales/fr';
import moment from 'moment';
import { useLocation, useNavigate, useSearchParams, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useReactiveVar } from '@apollo/client';

import { useSession } from '@hooks';
import { createBookingVar, refetchBookingsVar, refetchEventsVar } from '@context';
import {
  useListMyBookingsQuery,
  useListMyEventsQuery,
  ListMyBookingsQuery,
  ListMyEventsQuery,
} from '@graphql';
import * as helpers from './helpers';

interface DesktopCalendarProps {
  accountId: string;
  variant: 'all' | 'booking' | 'event';
}

type DateRange = { from: string; to: string } | null;
type ToDisplay = ListMyBookingsQuery['myBookings'] | ListMyEventsQuery['myEvents'];

export function DesktopCalendar(props: Readonly<DesktopCalendarProps>) {
  const { accountId, variant } = props;
  const { user, account } = useSession();
  const calendarRef = React.useRef<FullCalendar | null>(null);
  const [dateRange, setDateRange] = React.useState<DateRange | null>(null);
  const { eventId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname, search } = useLocation();
  const { t } = useTranslation('cta');
  const navigate = useNavigate();

  const isRefetchEvents = useReactiveVar(refetchEventsVar);
  const isRefetchBookings = useReactiveVar(refetchBookingsVar);

  const bookingQuery = useListMyBookingsQuery({
    skip: !dateRange || accountId !== account?.id || !['all', 'booking'].includes(variant),
    variables: dateRange ? { dateRange } : undefined,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const eventQuery = useListMyEventsQuery({
    skip: !dateRange || accountId !== account?.id || !['all', 'event'].includes(variant),
    variables: dateRange ? { dateRange } : undefined,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  const bookings = React.useMemo(() => bookingQuery.data?.myBookings ?? [], [bookingQuery.data?.myBookings]);
  const events = React.useMemo(() => eventQuery.data?.myEvents ?? [], [eventQuery.data?.myEvents]);

  const toDisplay = React.useMemo(() => {
    const formatedBookings = bookings.map((booking) => ({
      ...booking,
      start: helpers.getItemStart(booking),
      title: helpers.getItemTitle(booking, t),
      backgroundColor: helpers.getItemColor(booking),
      extendedProps: { ...booking },
    })) as ToDisplay;

    const formatedEvents = events.map((event) => ({
      ...event,
      start: event.from,
      title: event.title,
      extendedProps: { ...event },
    })) as ToDisplay;

    if (variant === 'booking') return formatedBookings;
    if (variant === 'event') return formatedEvents;
    return [...formatedBookings, ...formatedEvents] as ToDisplay;
  }, [variant, bookings, events, t]);

  // React.useEffect(() => {
  //   if (!dateRange) return;

  //   if (isRefetchEvents) {
  //     eventQuery.refetch({ dateRange }).finally(() => {
  //       refetchEventsVar(false);
  //     });
  //   }

  //   if (isRefetchBookings) {
  //     bookingQuery.refetch({ dateRange }).finally(() => {
  //       refetchBookingsVar(false);
  //     });
  //   }
  // }, [isRefetchEvents, isRefetchBookings, dateRange, eventQuery, bookingQuery]);

  React.useEffect(() => {
    const calendarApi = calendarRef.current?.getApi();

    if (!calendarApi) {
      return;
    }

    let date = searchParams.get('date');
    const isCalendarMode = searchParams.get('calendar') === 'true';

    if (!isCalendarMode) {
      searchParams.set('calendar', 'true');
    }

    // fallback to today if no date
    if (!date) {
      date = moment().format('YYYY-MM-DD');
      searchParams.set('date', date);
    }

    calendarApi.gotoDate(date);

    if (isCalendarMode || !searchParams.get('date')) {
      calendarApi.select({
        start: date,
        end: moment(date).add(1, 'day').format('YYYY-MM-DD'),
        allDay: true,
      });
    } else {
      calendarApi.unselect();
    }
  }, [searchParams]);

  function handleDateSelect(selectInfo: DateSelectArg) {
    const { startStr } = selectInfo;
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);

        next.set('date', moment(startStr).format('YYYY-MM-DD'));
        next.set('calendar', 'true');

        return next;
      },
      { replace: true }
    );
    // createBookingVar(true);
  }

  function handleDatesSet(dateInfo: DatesSetArg) {
    const { startStr } = dateInfo;
    const from = moment(startStr).startOf('month').toISOString();
    const to = moment(startStr).endOf('month').toISOString();
    setDateRange({ from, to });
  }

  function handleEventClick(clickInfo: any) {
    const { event } = clickInfo;
    const { __typename, ref } = event.extendedProps;

    // close popover if open
    const closeButton = document.querySelector('.fc-popover-close') as HTMLElement;
    if (closeButton) {
      closeButton.click();
    }

    const path = `/me/calendar/${__typename.toLocaleLowerCase()}/${ref}/detail`;
    navigate(path, { preventScrollReset: true, state: { from: pathname + search } });
  }

  function handleCreateBooking() {
    setSearchParams({ date: moment().format('YYYY-MM-DD'), calendar: 'true' });
    createBookingVar(true);
  }

  function handleCreateEvent() {
    const date = searchParams.get('date') ?? moment().format('YYYY-MM-DD');
    navigate(`/me/calendar/event/create?date=${date}`, { state: { from: pathname + search } });
  }

  function handleSelectAllow(selectInfo: DateSpanApi): boolean {
    const { startStr } = selectInfo;
    const dayIndex = moment(startStr).day();

    if (!account) {
      return false;
    }

    if (account.__typename === 'Spot') {
      const { opening, closing } = account;
      const dayOpenging = opening.find((day) => day.dayOfWeek === dayIndex);
      const isClosed = closing.some((range) => moment(startStr).isBetween(range.from, range.to));
      return !!dayOpenging?.openTime && !isClosed;
    }

    return account.__typename === 'Consumer';
  }

  function handleDayCellClassNames(args: DayCellContentArg) {
    const { date } = args;
    const classNames = [];
    const disableClassName = 'fc-day-disabled';
    const dayIndex = moment(date).day();

    if (account && account.__typename === 'Spot') {
      const { opening, closing } = account;
      const dayOpenging = opening.find((day) => day.dayOfWeek === dayIndex);
      const isClosed = closing.some((range) => moment(date).isBetween(range.from, range.to));

      if (!dayOpenging?.openTime || isClosed) {
        classNames.push(disableClassName);
      }
    }

    return classNames;
  }

  const locales = {
    fr: frLocale,
    es: esLocale,
    en: undefined,
  };
  const locale = user?.preferences.language
    ? locales[user?.preferences.language as 'fr' | 'es' | 'en']
    : undefined;

  const addButtonKey = variant === 'booking' ? 'addBookingButton' : 'addEventButton';
  const accountType = account?.__typename;

  return (
    <FullCalendar
      ref={calendarRef}
      loading={() => bookingQuery.loading || eventQuery.loading}
      height="100%"
      // contentHeight={'200px'} // to remove extra space below days with events
      // dayMaxEvents={1} // nb before cta "+x more"
      themeSystem="bootstrap5"
      firstDay={1} // monday
      locale={locale}
      plugins={[bootstrap5Plugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
      initialView="dayGridMonth"
      // add event button
      customButtons={{
        addBookingButton: {
          text: t('add a booking'),
          click: handleCreateBooking,
        },
        addEventButton: {
          text: t('add an event'),
          click: handleCreateEvent,
        },
      }}
      headerToolbar={{
        left: `prev,next${['Consumer', 'Spot'].includes(accountType ?? '') ? `,${addButtonKey}` : ''}`,
        center: 'title',
        right: 'today',
      }}
      views={{
        dayGridMonth: {
          dayMaxEventRows: 3,
          titleFormat: {
            year: 'numeric',
            month: 'long',
          },
        },
      }}
      events={toDisplay}
      editable={true}
      selectable={true}
      weekends={true}
      showNonCurrentDates={false}
      selectMirror={true}
      select={handleDateSelect}
      eventClick={handleEventClick}
      datesSet={handleDatesSet}
      selectAllow={handleSelectAllow}
      dayCellClassNames={handleDayCellClassNames}
      // update the db
      eventAdd={function () {}}
      eventChange={function () {}}
      eventRemove={function () {}}
    />
  );
}
